import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/AuthContext";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import {
     Box,
     Button,
     Chip,
     CircularProgress,
     TextField,
     Typography,
     useTheme,
} from "@mui/material";
import Image1 from '../../images/courses/image1.jpg';
import SnackbarAlert from "../../components/SnackBar";
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { buildFileFormData } from "../../helper/FileFormData";
import Fetch from "../../services/Fetch";
import { useNavigate, useParams } from "react-router-dom";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useConstants } from "../../hooks/UseConstants";
import PageContent from "../../components/PageContent";
import PageHero from "../../components/motion/PageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import ScrollReveal from "../../components/motion/ScrollReveal";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MovieCreationOutlinedIcon from "@mui/icons-material/MovieCreationOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { resolveCourseImage } from "../../helper/resolveMediaUrl";

function formatFileSize(bytes) {
     if (!bytes) return "0 B";
     if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
     return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileKind(file) {
     if (!file) return null;

     const mime = String(file.type || "").toLowerCase();
     const name = String(file.name || "").toLowerCase();

     if (mime === "application/pdf" || name.endsWith(".pdf")) return "pdf";
     if (
          mime.startsWith("video/")
          || [".mp4", ".mov", ".avi", ".webm", ".ogg", ".m3u8"].some((ext) => name.endsWith(ext))
     ) {
          return "video";
     }

     return "other";
}

function UpdateFile() {
     const { host, language } = useConstants();
     const isAr = language === "ar";
     const theme = useTheme();
     const { wait } = useContext(AuthContext);
     const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
     const { sendWait, setSendWait, getWait, setGetWait } = useWaits();
     const [file, setFile] = useState(null);
     const [content, setContent] = useState(null);
     const [course, setCourse] = useState(null);
     const [thumbnail, setThumbnail] = useState(null);
     const [thumbnailPreview, setThumbnailPreview] = useState("");
     const [nameEn, setNameEn] = useState("");
     const [nameAr, setNameAr] = useState("");
     const param = useParams();
     const navigate = useNavigate();

     const courseName = useMemo(() => {
          if (!course) return "";
          return isAr ? course.name_ar : course.name_en;
     }, [course, isAr]);

     const lessonName = useMemo(() => {
          if (!content) return "";
          return isAr ? content.name_ar : content.name_en;
     }, [content, isAr]);

     const courseImage = resolveCourseImage(course);
     const thumbnailSrc = thumbnailPreview || content?.thumbnail_url || "";
     const selectedFileKind = useMemo(() => getFileKind(file), [file]);
     const currentFileKind = content?.is_video ? "video" : (content ? "pdf" : null);
     const effectiveFileKind = selectedFileKind || currentFileKind;

     const getContent = async () => {
          setGetWait(true);
          const [fileResult, courseResult] = await Promise.all([
               Fetch(`${host}/courses/${param.course_id}/files/${param.file_id}/show`),
               Fetch(`${host}/courses/${param.course_id}/show`),
          ]);

          if (fileResult.status === 200) {
               const fileData = fileResult.data.data;
               setContent(fileData);
               setNameEn(fileData.name_en || "");
               setNameAr(fileData.name_ar || "");
          }

          if (courseResult.status === 200) {
               setCourse(courseResult.data.data);
          }

          setGetWait(false);
     };

     useEffect(() => {
          getContent();
     }, [param.course_id, param.file_id]);

     useEffect(() => {
          if (!thumbnail) {
               setThumbnailPreview("");
               return undefined;
          }
          const url = URL.createObjectURL(thumbnail);
          setThumbnailPreview(url);
          return () => URL.revokeObjectURL(url);
     }, [thumbnail]);

     const updateFile = async () => {
          if (!nameEn.trim() || !nameAr.trim()) {
               setSnackBar("error", isAr ? "أدخل اسم الدرس بالعربية والإنجليزية" : "Enter lesson name in Arabic and English");
               return;
          }
          if (selectedFileKind === "other") {
               setSnackBar("error", isAr ? "يسمح فقط بملفات الفيديو أو PDF" : "Only video or PDF files are allowed");
               return;
          }

          setSendWait(true);

          const formData = buildFileFormData({
               nameEn: nameEn.trim(),
               nameAr: nameAr.trim(),
               file,
               thumbnail,
               courseId: param.course_id,
          });

          const result = await Fetch(`${host}/teacher/courses/files/${param.file_id}/update`, 'POST', formData);

          if (result.status === 200) {
               setSnackBar('success', isAr ? 'تم تحديث الدرس بنجاح' : 'Lesson updated successfully');
               navigate(`/course-details/${param.course_id}`);
          } else if (result.status === 422) {
               setSnackBar('error', result.data.errors?.[0] || result.data.message);
          } else {
               setSnackBar('error', isAr ? 'تعذر تحديث الدرس' : 'Failed to update lesson');
          }

          setSendWait(false);
     };

     return (
          <>
               <PageContent wait={wait} loading={getWait}>
                    <PageHero
                         title={isAr ? "تعديل ملف الدورة" : "Update course file"}
                         subtitle={isAr ? "حدّث اسم الجلسة وصورتها أو استبدل الفيديو/ملف PDF" : "Edit lesson name, cover image, or replace the video/PDF file"}
                         icon={<OndemandVideoIcon />}
                         actions={
                              <>
                                   <Button
                                        variant="contained"
                                        startIcon={sendWait ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
                                        onClick={updateFile}
                                        disabled={sendWait}
                                        sx={{ bgcolor: "rgba(255,255,255,0.95)", color: "#0D358C", fontWeight: 700, "&:hover": { bgcolor: "#fff" } }}
                                   >
                                        {isAr ? "حفظ التعديلات" : "Save changes"}
                                   </Button>
                                   <Button
                                        variant="outlined"
                                        startIcon={<ArrowBackIcon />}
                                        onClick={() => navigate(`/course-details/${param.course_id}`)}
                                        sx={{ color: "white", borderColor: "rgba(255,255,255,0.6)", "&:hover": { borderColor: "white" } }}
                                   >
                                        {isAr ? "عودة للدورة" : "Back to course"}
                                   </Button>
                              </>
                         }
                    />

                    {course ? (
                         <ScrollReveal>
                              <Box
                                   sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        mb: 3,
                                        p: 2,
                                        borderRadius: 3,
                                        bgcolor: theme.palette.background.paper,
                                        border: "1px solid",
                                        borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                                   }}
                              >
                                   <Box
                                        sx={{
                                             width: 72,
                                             height: 72,
                                             borderRadius: 2,
                                             overflow: "hidden",
                                             flexShrink: 0,
                                             bgcolor: "#0D358C",
                                             display: "flex",
                                             alignItems: "center",
                                             justifyContent: "center",
                                        }}
                                   >
                                        {courseImage ? (
                                             <Box component="img" src={courseImage} alt={courseName} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                        ) : (
                                             <MenuBookOutlinedIcon sx={{ color: "white", fontSize: 36 }} />
                                        )}
                                   </Box>
                                   <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                             {isAr ? "الدورة المرتبطة" : "Linked course"}
                                        </Typography>
                                        <Typography variant="h6" fontWeight={800} sx={{ color: "#0D358C" }} noWrap>
                                             {courseName}
                                        </Typography>
                                        <Chip size="small" label={`ID #${course.id}`} sx={{ mt: 0.5 }} />
                                   </Box>
                              </Box>
                         </ScrollReveal>
                    ) : null}

                    <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, direction: "ltr", gap: 3, alignItems: "stretch" }}>
                         <ScrollReveal delay={50} className="w-full lg:w-[38%]">
                              <Box
                                   sx={{
                                        height: "100%",
                                        minHeight: { lg: 420 },
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 2,
                                        p: 4,
                                        borderRadius: 4,
                                        border: "1px solid",
                                        borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                                        background: theme.palette.mode === "dark"
                                             ? "linear-gradient(160deg, #1a2744 0%, #121212 100%)"
                                             : "linear-gradient(160deg, #e8f0fa 0%, #f5f8fc 100%)",
                                   }}
                              >
                                   <Box
                                        component="img"
                                        src={Image1}
                                        alt=""
                                        sx={{ width: "100%", maxWidth: 280, height: "auto", objectFit: "contain" }}
                                   />
                                   <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", maxWidth: 300, lineHeight: 1.8 }}>
                                        {isAr
                                                  ? "يمكنك تعديل اسم الجلسة وصورتها، أو استبدال الملف الحالي بفيديو أو PDF. إذا لم تختر ملفاً جديداً فسيبقى الملف الحالي كما هو."
                                                  : "You can update the lesson name and image, or replace the current file with a video or PDF. If no new file is selected, the current file stays unchanged."}
                                   </Typography>
                                   {lessonName ? (
                                        <Chip
                                             icon={<MovieCreationOutlinedIcon />}
                                             label={lessonName}
                                             sx={{ maxWidth: "100%", "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }}
                                        />
                                   ) : null}
                              </Box>
                         </ScrollReveal>

                         <Box sx={{ flex: 1, minWidth: 0, direction: isAr ? "rtl" : "ltr", display: "flex", flexDirection: "column", gap: 3 }}>
                              <ScrollReveal delay={100}>
                                   <DashboardPanelCard
                                        title={isAr ? "بيانات الدرس" : "Lesson details"}
                                        subtitle={isAr ? "حدّث الاسم الظاهر للطلاب في قوائم الدروس" : "Update the name shown to students in lesson lists"}
                                   >
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                             <TextField
                                                  value={nameEn}
                                                  onChange={(e) => setNameEn(e.target.value)}
                                                  label={isAr ? "اسم الدرس بالإنجليزية" : "Lesson name (English)"}
                                                  variant="outlined"
                                                  size="small"
                                                  fullWidth
                                                  required
                                             />
                                             <TextField
                                                  value={nameAr}
                                                  onChange={(e) => setNameAr(e.target.value)}
                                                  label={isAr ? "اسم الدرس بالعربية" : "Lesson name (Arabic)"}
                                                  variant="outlined"
                                                  size="small"
                                                  fullWidth
                                                  required
                                             />
                                        </Box>
                                   </DashboardPanelCard>
                              </ScrollReveal>

                              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" }, gap: 3 }}>
                                   <ScrollReveal delay={150}>
                                        <DashboardPanelCard
                                             title={isAr ? "الصورة المصغرة" : "Thumbnail"}
                                             subtitle={isAr ? "اختيارية وتُعرض مع الجلسة" : "Optional and displayed with the lesson"}
                                        >
                                             <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                                                  <Box
                                                       component="label"
                                                       sx={{
                                                            position: "relative",
                                                            width: 180,
                                                            height: 112,
                                                            borderRadius: 2,
                                                            border: "2px dashed",
                                                            borderColor: thumbnailSrc ? "#0D358C" : "#94a3b8",
                                                            overflow: "hidden",
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f8fafc",
                                                            "&:hover": { borderColor: "#0D358C" },
                                                       }}
                                                  >
                                                       {thumbnailSrc ? (
                                                            <Box component="img" src={thumbnailSrc} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                                       ) : (
                                                            <AddPhotoAlternateIcon sx={{ fontSize: 40, color: "text.secondary" }} />
                                                       )}
                                                       <input
                                                            type="file"
                                                            accept="image/*"
                                                            hidden
                                                            onChange={(e) => setThumbnail(e.target.files?.[0] || null)}
                                                       />
                                                  </Box>
                                                  <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "center" }}>
                                                       {isAr ? "انقر لاختيار صورة جديدة أو اترك الحالية كما هي" : "Click to choose a new image or keep the current one"}
                                                  </Typography>
                                             </Box>
                                        </DashboardPanelCard>
                                   </ScrollReveal>

                                   <ScrollReveal delay={200}>
                                        <DashboardPanelCard
                                             title={isAr ? "استبدال ملف الجلسة" : "Replace lesson file"}
                                             subtitle={isAr ? "اختياري: ارفع فيديو أو PDF جديداً ليحل محل الملف الحالي" : "Optional: upload a new video or PDF to replace the current file"}
                                        >
                                             <Box
                                                  component="label"
                                                  sx={{
                                                       display: "flex",
                                                       flexDirection: "column",
                                                       alignItems: "center",
                                                       justifyContent: "center",
                                                       gap: 1.5,
                                                       p: 3,
                                                       borderRadius: 3,
                                                       border: "2px dashed",
                                                       borderColor: file ? "#2563eb" : "#cbd5e1",
                                                       bgcolor: file
                                                            ? (theme.palette.mode === "dark" ? "rgba(37,99,235,0.12)" : "#eff6ff")
                                                            : (theme.palette.mode === "dark" ? "background.default" : "#f8fafc"),
                                                       cursor: "pointer",
                                                       transition: "all 0.2s",
                                                       "&:hover": { borderColor: "#0D358C", bgcolor: theme.palette.mode === "dark" ? "rgba(13,53,140,0.15)" : "#eef2ff" },
                                                  }}
                                             >
                                                  <CloudUploadIcon sx={{ fontSize: 44, color: file ? "#2563eb" : "#64748b" }} />
                                                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#0D358C", textAlign: "center" }}>
                                                       {file
                                                            ? (isAr ? "تم اختيار ملف جديد" : "New file selected")
                                                            : (isAr ? "اسحب الملف هنا أو انقر للاختيار" : "Drag the file here or click to browse")}
                                                  </Typography>
                                                  <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
                                                       {effectiveFileKind === "pdf"
                                                            ? (isAr ? "يمكنك استبدال الملف الحالي بملف PDF جديد أو فيديو عند الحاجة" : "You can replace the current file with a new PDF or video when needed")
                                                            : (isAr ? "إن لم تختَر ملفاً جديداً فلن يتم تغيير الملف الحالي" : "If you do not choose a new file, the current file remains unchanged")}
                                                  </Typography>
                                                  <input
                                                       type="file"
                                                       hidden
                                                       accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,.mp4,.mov,.avi,.webm,application/pdf,.pdf"
                                                       onChange={(e) => setFile(e.target.files?.[0] || null)}
                                                  />
                                             </Box>

                                             {file ? (
                                                  <Box
                                                       sx={{
                                                            mt: 2,
                                                            p: 2,
                                                            borderRadius: 2,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 2,
                                                            bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f1f5f9",
                                                       }}
                                                  >
                                                       {selectedFileKind === "pdf"
                                                            ? <PictureAsPdfOutlinedIcon sx={{ color: "#0D358C", fontSize: 32 }} />
                                                            : <MovieCreationOutlinedIcon sx={{ color: "#0D358C", fontSize: 32 }} />}
                                                       <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography variant="body2" fontWeight={700} noWrap>
                                                                 {file.name}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                                 {formatFileSize(file.size)}
                                                                 {selectedFileKind === "pdf"
                                                                      ? (isAr ? " • ملف PDF" : " • PDF document")
                                                                      : selectedFileKind === "video"
                                                                           ? (isAr ? " • ملف فيديو" : " • video file")
                                                                           : ""}
                                                            </Typography>
                                                       </Box>
                                                  </Box>
                                             ) : null}
                                        </DashboardPanelCard>
                                   </ScrollReveal>
                              </Box>

                              <ScrollReveal delay={250}>
                                   <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, flexWrap: "wrap" }}>
                                        <Button variant="outlined" onClick={() => navigate(`/course-details/${param.course_id}`)} disabled={sendWait}>
                                             {isAr ? "إلغاء" : "Cancel"}
                                        </Button>
                                        <Button
                                             variant="contained"
                                             onClick={updateFile}
                                             disabled={sendWait}
                                             startIcon={sendWait ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />}
                                             sx={{ minWidth: 200, bgcolor: "#0D358C", "&:hover": { bgcolor: "#1d4ed8" } }}
                                        >
                                             {sendWait ? (isAr ? "جاري الحفظ..." : "Saving...") : (isAr ? "حفظ التعديلات" : "Save changes")}
                                        </Button>
                                   </Box>
                              </ScrollReveal>
                         </Box>
                    </Box>
               </PageContent>
               <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
          </>
     );
}

export default UpdateFile;